import React from "react";

import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { Column, Row, StackProps } from "@rui/Stack";
import { Img } from "@rui/styles";

const BaseContainer = styled(Column)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.corners.base,
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[50],
  padding: theme.spaces.medium,
  minHeight: "150px",
  overflow: "hidden",
  justifyContent: "space-between",
  flex: "1 1 0px",
}));

type ContainerProps = StackProps & { color?: React.CSSProperties["color"] };

export const Container = ({ color, children, ...props }: ContainerProps) => (
  <BaseContainer spacing={2} {...props}>
    {color && <GradientCircles color={color} />}
    {children}
  </BaseContainer>
);

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.bodyPrimaryMed,
  color: theme.palette.shades.neutral[800],
}));

export const UpsellModuleTitle = ({
  Icon,
  children,
}: {
  Icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Row variant="left" spacing={1} overflow="hidden" flexShrink={1} width="100%">
    {Icon}
    <Title noWrap>{children}</Title>
  </Row>
);

export const LeftContentContainer = styled(Column)({
  justifyContent: "space-between",
  flex: 1,
  overflow: "hidden",
});

export const UpsellModuleThumbnail = styled(Img)(({ theme }) => ({
  borderRadius: theme.corners.small,
  width: "120px",
  objectFit: "cover",
  height: "80px",
}));

interface GradientCirclesProps {
  /* Base color of gradient */
  color: React.CSSProperties["color"];
}

const GradientCircles = styled<React.FC<GradientCirclesProps>>(
  ({ color: _c, ...props }) => (
    <Column {...props}>
      <Column className="gradient-circle__middle">
        <Column className="gradient-circle__inner">&nbsp;</Column>
      </Column>
    </Column>
  )
)(({ color, theme }) => ({
  position: "absolute",
  width: "300px",
  height: "300px",
  top: -12,
  right: -125,
  zIndex: 0,

  "&, .gradient-circle__middle, .gradient-circle__inner": {
    borderRadius: theme.corners.round,
    backgroundColor: color,
    alignItems: "center",
    justifyContent: "center",
  },
  "& .gradient-circle__middle": {
    width: "185px",
    height: "185px",
  },
  "& .gradient-circle__inner": {
    width: "83px",
    height: "83px",
  },
}));

export const Action = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  whiteSpace: "nowrap",
  "&.Mui-disabled": {
    backgroundColor: theme.palette.shades.neutral[50],
    borderColor: theme.palette.shades.neutral[50],
    color: theme.palette.shades.neutral[400],
  },
}));
